/**
 * Home tab, /lms (plan docs/plans/lms-plan.md 7.1, 9.4, section 13 tab 1;
 * R1, R3, R4, R24; WP-41). The first screen under /lms, three blocks in this
 * order, dense per the design skill:
 *
 *   1. "Your to-do"  open assignments by due date: why each was assigned in
 *                    words, the due state, minutes, Start or Continue (TodoList)
 *   2. "In progress" the started ones, from their slide bookmarks (InProgressList)
 *   3. "Recent"      the last five completions with Download certificate (RecentList)
 *
 * and a right-hand column (stacked below on narrow screens) with the
 * onboarding checklist card while a run is active (OnboardingCard) and the
 * notifications inbox (components/NotificationInbox). Empty states use
 * EmptyState. Each block loads on its own (useLoad), so one failing read
 * shows its own Retry and never blanks the page (G10).
 *
 * LmsApp routes the index route to this file's DEFAULT export (a component
 * with no required props; WP-03's registry rule).
 */
import { useState } from 'react';
import PageHeader, { RefreshButton } from '../../../ui/PageHeader';
import { lmsPageCls } from '../../components/layout/page';
import { NotificationInbox } from '../../components/NotificationInbox';
import { getMyOnboarding } from '../../api/me';
import type { MyOnboarding } from '../../api/me';
import type { TranscriptItem } from '../../api/completions';
import { lmsTab } from '../config';
import { InProgressList } from './InProgressList';
import { OnboardingCard } from './OnboardingCard';
import { RecentList } from './RecentList';
import { TodoList } from './TodoList';
import { loadRecent, loadTodo } from './homeData';
import type { TodoData } from './homeData';
import { useLoad } from './useLoad';

const TAB = lmsTab('home');

export function HomeTab() {
  const [reloadKey, setReloadKey] = useState(0);
  const todo = useLoad<TodoData>(loadTodo, 'Your to-do list could not be loaded.', reloadKey);
  const recent = useLoad<TranscriptItem[]>(loadRecent, 'Your recent completions could not be loaded.', reloadKey);
  // A failed onboarding read hides the card (it is shown only while a run is active); the
  // Onboarding tab itself shows the error with its own Retry.
  const onboarding = useLoad<MyOnboarding | null>(getMyOnboarding, 'Your onboarding checklist could not be loaded.', reloadKey);

  const refreshing = todo.loading || recent.loading || onboarding.loading;

  return (
    <div className={lmsPageCls} data-testid="lms-home">
      <PageHeader
        title={TAB.title}
        icon={TAB.icon}
        subtitle={TAB.description}
        actions={<RefreshButton onClick={() => setReloadKey((k) => k + 1)} loading={refreshing} label="Refresh Home" />}
      />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_20rem] xl:grid-cols-[minmax(0,1fr)_24rem]">
        <div className="min-w-0 space-y-4">
          <TodoList todo={todo} />
          <InProgressList todo={todo} />
          <RecentList recent={recent} />
        </div>
        <aside className="min-w-0 space-y-4" aria-label="Inbox and onboarding">
          {onboarding.data && onboarding.data.run?.status === 'active' ? <OnboardingCard onboarding={onboarding.data} /> : null}
          <NotificationInbox reloadKey={reloadKey} />
        </aside>
      </div>
    </div>
  );
}

export default HomeTab;
